/**
 * 👨‍🚀 L'ÉQUIPAGE DU VAISSEAU - Les 3 Commandants
 * 
 * ORDRE CORRECT :
 * 1. Rejoignez l'Équipage (3 Commandants) - EN PREMIER !
 * 2. Nos Engagements Galactiques
 * 3. L'histoire de votre épicerie
 * 4. Transmission Snap
 * 5. Télécharge l'Application
 * 6. Informations légales
 * 7. Coordonnées
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Star, MapPin, Phone, Radio, Zap, Smartphone, AlertTriangle } from 'lucide-react';
import QRCode from 'react-qr-code';

// 🎨 AVATARS DES COMMANDANTS - CORPS ENTIER EN TENUE SPATIALE
const COMMANDANT_MOMO = 'https://static.prod-images.emergentagent.com/jobs/c755df4b-ba62-49b1-8c90-60a0d80179aa/images/589762455beef5726d9300abd04aeca20786acff5b6c4db5b7833fe4eade789e.png';
const COMMANDANT_JEANPHI = 'https://static.prod-images.emergentagent.com/jobs/c755df4b-ba62-49b1-8c90-60a0d80179aa/images/a5a8f3b932bbd1759e96db90f283f2982696de5d3ad6928188279e219edb3376.png';
const COMMANDANTE_PIXIE = 'https://static.prod-images.emergentagent.com/jobs/27270b21-de5c-490c-bb2d-69a28ad2ffcf/images/86475b0f2942e3f2167ec475898d0a6672cd55ad1034dafb9fdc8596854c706d.png';

const CommandantsSection = () => {
  return (
    <section className="relative py-16 px-4 overflow-hidden">
      {/* Effet de fond spatial */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 via-transparent to-cyan-900/10 pointer-events-none" />

      <div className="max-w-5xl mx-auto relative space-y-20">
        
        {/* ═══════════════════════════════════════════════════════════════
            SECTION 1: REJOIGNEZ L'ÉQUIPAGE - LES 3 COMMANDANTS (EN PREMIER !)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-10"
        >
          {/* Titre spatial */}
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 mb-4"
            >
              <Rocket className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-bold uppercase tracking-wider">Station Vermelles-62980</span>
            </motion.div>
            
            <h2 className="text-3xl md:text-4xl font-black text-white">
              🚀 Rejoignez <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400">l'Équipage</span>
            </h2>
          </div>

          {/* Les 3 Commandants - GRADES SPATIAUX ! */}
          <div className="grid md:grid-cols-3 gap-6 items-end">
            
            {/* 1️⃣ JEAN-PHI - Second & Bras Droit (1m79 - PLUS GRAND !) */}
            <motion.div
              className="relative text-center group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {/* Badge Grade */}
              <motion.div 
                className="mb-3 flex justify-center"
                initial={{ y: -10, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold shadow-lg shadow-cyan-500/30 flex flex-col items-center">
                  <span className="flex items-center gap-1">🎖️ COMMANDANT</span>
                  <span className="text-[10px] text-cyan-200">Second & Bras Droit</span>
                </div>
              </motion.div>
              
              {/* Avatar - PLUS GRAND car 1m79 */}
              <motion.div
                className="relative mx-auto mb-4 overflow-hidden rounded-2xl"
                style={{ 
                  width: '220px', 
                  height: '340px',
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(6, 182, 212, 0.05))',
                  border: '2px solid rgba(59, 130, 246, 0.4)',
                  boxShadow: '0 0 40px rgba(59, 130, 246, 0.25)'
                }}
                whileHover={{ scale: 1.02, boxShadow: '0 0 60px rgba(59, 130, 246, 0.4)' }}
              >
                <img 
                  src={COMMANDANT_JEANPHI}
                  alt="Commandant Jean-Phi"
                  className="w-full h-full object-cover object-top"
                />
              </motion.div>
              
              {/* Nom + Grade */}
              <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Commandant J-Phi
              </h3>
              <p className="text-cyan-400/70 text-xs mt-1 font-semibold uppercase tracking-wider">Second & Bras Droit</p>
              
              {/* 5 Étoiles - aussi important ! */}
              <div className="mt-2 flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </motion.div>

            {/* 2️⃣ MOMO - Commandant en Chef / Le Patron (1m67 - plus petit mais LE CHEF !) */}
            <motion.div
              className="relative text-center group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {/* Badge Grade CHEF */}
              <motion.div 
                className="mb-3 flex justify-center"
                initial={{ y: -10, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 text-white text-xs font-bold shadow-lg shadow-amber-500/40 flex flex-col items-center"
                  style={{ boxShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}
                >
                  <span className="flex items-center gap-1">👑 COMMANDANT EN CHEF</span>
                  <span className="text-[10px] text-amber-100">Le Patron</span>
                </div>
              </motion.div>
              
              {/* Avatar - 1m67 donc un peu plus petit que Jean-Phi */}
              <motion.div
                className="relative mx-auto mb-4 overflow-hidden rounded-2xl"
                style={{ 
                  width: '210px', 
                  height: '320px',
                  background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.12), rgba(234, 88, 12, 0.06))',
                  border: '2px solid rgba(251, 191, 36, 0.4)',
                  boxShadow: '0 0 40px rgba(251, 191, 36, 0.25)'
                }}
                whileHover={{ scale: 1.02, boxShadow: '0 0 60px rgba(251, 191, 36, 0.4)' }}
              >
                <img 
                  src={COMMANDANT_MOMO}
                  alt="Commandant Momo"
                  className="w-full h-full object-cover object-top"
                />
                
                {/* Couronne flottante */}
                <motion.div
                  className="absolute -top-2 left-1/2 -translate-x-1/2 text-2xl"
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  👑
                </motion.div>
              </motion.div>
              
              {/* Nom + Grade */}
              <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400">
                Commandant Momo
              </h3>
              <p className="text-amber-400/80 text-xs mt-1 font-bold uppercase tracking-wider">👑 Commandant en Chef</p>
              
              {/* 5 Étoiles */}
              <div className="mt-2 flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </motion.div>

            {/* 3️⃣ PIXIE - Au Service des Commandants */}
            <motion.div
              className="relative text-center group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {/* Badge Grade */}
              <motion.div 
                className="mb-3 flex justify-center"
                initial={{ y: -10, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <div className="px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold shadow-lg shadow-pink-500/30 flex flex-col items-center">
                  <span className="flex items-center gap-1">🧚‍♀️ COMMANDANTE</span>
                  <span className="text-[10px] text-pink-200">Station Orbitale</span>
                </div>
              </motion.div>
              
              {/* Avatar */}
              <motion.div
                className="relative mx-auto mb-4 overflow-hidden rounded-2xl"
                style={{ 
                  width: '210px', 
                  height: '330px',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1), rgba(168, 85, 247, 0.05))',
                  border: '2px solid rgba(236, 72, 153, 0.4)',
                  boxShadow: '0 0 40px rgba(236, 72, 153, 0.25)'
                }}
                whileHover={{ scale: 1.02, boxShadow: '0 0 60px rgba(236, 72, 153, 0.4)' }}
              >
                <img 
                  src={COMMANDANTE_PIXIE}
                  alt="Commandante Pixie"
                  className="w-full h-full object-cover object-top"
                />
              </motion.div>
              
              {/* Nom + Grade */}
              <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">
                Commandante Pixie
              </h3>
              <p className="text-pink-400/70 text-xs mt-1 font-semibold uppercase tracking-wider">Station Orbitale LPE</p>
              
              {/* 5 Étoiles - aussi importante ! */}
              <div className="mt-2 flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Texte court */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-gray-400 max-w-2xl mx-auto"
          >
            <span className="text-cyan-400 font-semibold">La Petite Épicerie</span> à <span className="text-pink-400">Vermelles</span> — 
            <span className="text-yellow-400 font-bold"> 5 étoiles sur Google</span> ⭐
          </motion.p>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 2: L'HISTOIRE DE VOTRE ÉPICERIE (AVANT Engagements !)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-6"
        >
          <h2 className="text-2xl font-black text-white text-center">
            L'histoire de <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-400">votre épicerie</span>
          </h2>

          {/* Cadre transparent spatial */}
          <motion.div 
            className="max-w-3xl mx-auto p-6 rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(6, 182, 212, 0.05))',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              boxShadow: '0 0 30px rgba(168, 85, 247, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
            }}
            whileHover={{ boxShadow: '0 0 40px rgba(168, 85, 247, 0.2)' }}
          >
            <div className="space-y-4 text-gray-300 leading-relaxed text-center">
              <p>
                <span className="text-pink-400 font-semibold">La Petite Épicerie</span>, c'est avant tout{' '}
                <span className="text-amber-400 font-bold">Momo & Jean-Phi</span>, deux commerçants passionnés qui ont fait le pari fou d'ouvrir une épicerie de quartier à{' '}
                <span className="text-cyan-400 font-semibold">Vermelles</span> (62980).
              </p>

              <p>
                Leur secret ?{' '}
                <span className="text-green-400 font-semibold">Un accueil chaleureux</span>, une{' '}
                <span className="underline decoration-purple-400 decoration-2 underline-offset-2">gamme de produits étonnante</span>{' '}
                pour tous les goûts, et surtout une{' '}
                <span className="text-pink-400 font-semibold">écoute exceptionnelle</span>{' '}
                de leurs clients.
              </p>

              <p className="italic text-gray-400">
                Résultat ?{' '}
                <span className="text-yellow-400 font-bold not-italic">5 étoiles sur Google</span>{' '}
                et une communauté de fidèles qui ne jurent que par cette petite épicerie pas comme les autres !{' '}
                <span className="text-xl">✨</span>
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 3: NOS ENGAGEMENTS GALACTIQUES
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-6"
        >
          <h2 className="text-2xl font-black text-white text-center flex items-center justify-center gap-3">
            <Zap className="w-6 h-6 text-yellow-400" />
            Nos Engagements Galactiques
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* 7j/7 */}
            <motion.div
              className="p-5 rounded-2xl text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.2), rgba(71, 85, 105, 0.1))',
                border: '1px solid rgba(148, 163, 184, 0.2)'
              }}
              whileHover={{ scale: 1.03, y: -3 }}
            >
              <div className="text-3xl mb-2">🕐</div>
              <h3 className="text-white font-bold text-lg">7j/7</h3>
              <p className="text-gray-400 text-sm">Midi - Minuit</p>
            </motion.div>

            {/* Livraison GRATUITE */}
            <motion.div
              className="p-5 rounded-2xl text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.08))',
                border: '1px solid rgba(34, 197, 94, 0.25)'
              }}
              whileHover={{ scale: 1.03, y: -3 }}
            >
              <div className="text-3xl mb-2">🚀</div>
              <h3 className="text-white font-bold text-lg">Livraison GRATUITE</h3>
              <p className="text-gray-400 text-sm">🌙 Ramadan: Ven & Sam 21h-4h !</p>
            </motion.div>

            {/* Service Humain */}
            <motion.div
              className="p-5 rounded-2xl text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(219, 39, 119, 0.08))',
                border: '1px solid rgba(236, 72, 153, 0.25)'
              }}
              whileHover={{ scale: 1.03, y: -3 }}
            >
              <div className="text-3xl mb-2">💖</div>
              <h3 className="text-white font-bold text-lg">Service Humain</h3>
              <p className="text-gray-400 text-sm">Toujours là</p>
            </motion.div>

            {/* Prix Malins */}
            <motion.div
              className="p-5 rounded-2xl text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(139, 92, 246, 0.08))',
                border: '1px solid rgba(168, 85, 247, 0.25)'
              }}
              whileHover={{ scale: 1.03, y: -3 }}
            >
              <div className="text-3xl mb-2">💎</div>
              <h3 className="text-white font-bold text-lg">Prix Malins</h3>
              <p className="text-gray-400 text-sm">Promos régulières</p>
            </motion.div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 4: TRANSMISSION SNAP (SPACE MOOD)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center space-y-6"
        >
          <div className="flex items-center justify-center gap-2">
            <Radio className="w-5 h-5 text-purple-400 animate-pulse" />
            <h2 className="text-2xl font-black text-white">
              Transmission <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Snap</span>
            </h2>
          </div>

          <p className="text-gray-400">
            Captez nos signaux exclusifs : offres secrètes, coulisses, nouveautés... 📡
          </p>

          {/* Carte Snapchat SPACE MOOD */}
          <motion.a
            href="https://www.snapchat.com/@lapetiteepi62"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div 
              className="p-6 rounded-2xl flex items-center gap-6"
              style={{
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(139, 92, 246, 0.15), rgba(99, 102, 241, 0.1))',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                boxShadow: '0 10px 40px rgba(168, 85, 247, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* QR Code */}
              <div 
                className="p-3 rounded-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)'
                }}
              >
                <QRCode 
                  value="https://www.snapchat.com/@lapetiteepi62" 
                  size={100}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#1a1a2e"
                />
              </div>

              {/* Infos */}
              <div className="text-left">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">👻</span>
                  <span className="text-white font-black text-xl">@lapetiteepice62</span>
                </div>
                <p className="text-gray-300 text-sm mb-3">
                  Scanne le QR code ou clique pour nous ajouter !
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-purple-500/30 text-purple-200 text-xs font-medium border border-purple-500/30">
                    🎁 Offres exclu
                  </span>
                  <span className="px-3 py-1 rounded-full bg-pink-500/30 text-pink-200 text-xs font-medium border border-pink-500/30">
                    📸 Coulisses
                  </span>
                  <span className="px-3 py-1 rounded-full bg-cyan-500/30 text-cyan-200 text-xs font-medium border border-cyan-500/30">
                    🆕 Nouveautés
                  </span>
                </div>
              </div>
            </div>
          </motion.a>

          {/* Liens Instagram et Facebook */}
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <motion.a
              href="https://www.instagram.com/la_petite_epicerie.shop/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-pink-500/40 text-pink-300 font-medium hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
            >
              <span className="text-xl">📸</span> Instagram
            </motion.a>
            <motion.a
              href="https://www.facebook.com/profile.php?id=61588543479522"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-500/20 border border-blue-500/40 text-blue-300 font-medium hover:bg-blue-500/30 transition-all"
            >
              <span className="text-xl">👤</span> Facebook
            </motion.a>
          </div>

          <p className="text-gray-500 text-sm mt-6">
            + de <span className="text-purple-400 font-bold">500 cosmonautes</span> nous suivent déjà ! 🛸
          </p>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 5: INFORMATIONS LÉGALES (3 AVERTISSEMENTS)
            Note: La section "Télécharger l'App" est maintenant dans DownloadAppSection
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-4"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <span className="text-gray-400 text-sm font-medium uppercase tracking-wider">Informations légales</span>
          </div>

          <div className="grid md:grid-cols-3 gap-4 text-center">
            {/* Alcool */}
            <div 
              className="p-4 rounded-xl"
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)'
              }}
            >
              <p className="text-red-400 text-xs font-medium">
                🍷 L'abus d'alcool est dangereux pour la santé. À consommer avec modération.
              </p>
            </div>

            {/* CBD */}
            <div 
              className="p-4 rounded-xl"
              style={{
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.2)'
              }}
            >
              <p className="text-green-400 text-xs font-medium">
                🌿 **CBD : Produits conformes à la législation française (&lt;0.3% THC). Déconseillé aux femmes enceintes.
              </p>
            </div>

            {/* Puffs */}
            <div 
              className="p-4 rounded-xl"
              style={{
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.2)'
              }}
            >
              <p className="text-purple-400 text-xs font-medium">
                💨 *Puffs : Produits destinés aux adultes (+18). Contient de la nicotine. Rechargeables uniquement.
              </p>
            </div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 6: TÉLÉCHARGER L'APP (JUSTE AVANT STATION ACTIVE)
            🚀 VERSION DINGUERIE SPATIALE !
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative py-12 px-6 rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(26, 10, 46, 0.9) 50%, rgba(15, 12, 41, 0.95) 100%)',
            border: '3px solid transparent',
            borderImage: 'linear-gradient(135deg, #00ffff, #ff00ff, #00ffff) 1',
            boxShadow: '0 0 60px rgba(0, 255, 255, 0.3), 0 0 100px rgba(255, 0, 255, 0.2), inset 0 0 60px rgba(139, 92, 246, 0.1)',
          }}
        >
          {/* 🌟 Étoiles et particules de fond */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(40)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: Math.random() > 0.7 ? '3px' : '2px',
                  height: Math.random() > 0.7 ? '3px' : '2px',
                  background: Math.random() > 0.5 ? '#00ffff' : '#ff00ff',
                }}
                animate={{
                  opacity: [0.2, 1, 0.2],
                  scale: [1, 1.8, 1],
                  boxShadow: ['0 0 2px currentColor', '0 0 8px currentColor', '0 0 2px currentColor'],
                }}
                transition={{
                  duration: 1.5 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>

          {/* Effet glow animé coins */}
          <motion.div
            className="absolute -top-20 -left-20 w-40 h-40 rounded-full blur-3xl"
            style={{ background: 'rgba(0, 255, 255, 0.4)' }}
            animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          <motion.div
            className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl"
            style={{ background: 'rgba(255, 0, 255, 0.4)' }}
            animate={{ scale: [1.3, 1, 1.3], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
          />

          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-10">
            
            {/* 🎯 QR CODE HIGH-TECH NÉON ROSE-BLEU */}
            <div className="flex-shrink-0">
              <motion.div
                className="relative"
                whileHover={{ scale: 1.03 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                {/* Glow externe pulsant */}
                <motion.div 
                  className="absolute -inset-6 rounded-3xl opacity-60"
                  style={{
                    background: 'radial-gradient(circle, rgba(236, 72, 153, 0.6) 0%, rgba(59, 130, 246, 0.4) 50%, transparent 70%)',
                    filter: 'blur(20px)',
                  }}
                  animate={{ 
                    scale: [1, 1.15, 1],
                    opacity: [0.4, 0.7, 0.4],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />

                {/* Bordure néon animée */}
                <div 
                  className="absolute -inset-1 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, #ec4899, #3b82f6, #ec4899, #3b82f6)',
                    backgroundSize: '300% 300%',
                    animation: 'neon-border 2s ease infinite',
                  }}
                />
                
                {/* Conteneur principal du QR */}
                <div 
                  className="relative p-5 rounded-xl"
                  style={{
                    background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
                  }}
                >
                  {/* Badge HIGH-TECH */}
                  <motion.div
                    className="absolute -top-5 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full font-black text-sm whitespace-nowrap z-20"
                    style={{
                      background: 'linear-gradient(135deg, #ec4899, #8b5cf6, #3b82f6)',
                      boxShadow: '0 0 30px rgba(236, 72, 153, 0.8), 0 0 60px rgba(59, 130, 246, 0.5)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                    }}
                    animate={{ 
                      boxShadow: [
                        '0 0 30px rgba(236, 72, 153, 0.8), 0 0 60px rgba(59, 130, 246, 0.5)',
                        '0 0 50px rgba(59, 130, 246, 0.8), 0 0 80px rgba(236, 72, 153, 0.5)',
                        '0 0 30px rgba(236, 72, 153, 0.8), 0 0 60px rgba(59, 130, 246, 0.5)',
                      ],
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <span className="text-white">📲 SCANNE-MOI</span>
                  </motion.div>
                  
                  {/* QR Code avec gradient néon */}
                  <div 
                    className="relative p-1 rounded-lg overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #ec4899, #3b82f6)',
                    }}
                  >
                    <div 
                      className="p-4 rounded-md relative"
                      style={{
                        background: '#ffffff',
                      }}
                    >
                      {/* Overlay gradient sur le QR pour effet néon */}
                      <div 
                        className="absolute inset-0 pointer-events-none z-10 rounded-md"
                        style={{
                          background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(59, 130, 246, 0.15))',
                          mixBlendMode: 'overlay',
                        }}
                      />
                      {/* Effet scan lumineux */}
                      <motion.div
                        className="absolute inset-0 pointer-events-none z-20"
                        style={{
                          background: 'linear-gradient(180deg, transparent 0%, rgba(236, 72, 153, 0.3) 50%, transparent 100%)',
                          height: '30%',
                        }}
                        animate={{ 
                          top: ['-30%', '100%'],
                        }}
                        transition={{ 
                          duration: 2, 
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                      />
                      <div style={{ 
                        background: 'linear-gradient(135deg, #ec4899, #7c3aed, #3b82f6)',
                        WebkitBackgroundClip: 'text',
                        padding: '2px',
                      }}>
                        <QRCode
                          value="https://lapetiteepicerie.shop"
                          size={150}
                          level="H"
                          bgColor="#ffffff"
                          fgColor="#1a1a2e"
                          style={{
                            borderRadius: '4px',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Barre de statut HIGH-TECH */}
                  <div className="mt-4 flex items-center justify-center gap-3">
                    <motion.div
                      className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold"
                      style={{
                        background: 'rgba(236, 72, 153, 0.2)',
                        border: '1px solid rgba(236, 72, 153, 0.5)',
                      }}
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                      <span className="text-pink-400">LIVE</span>
                    </motion.div>
                    <div className="h-3 w-px bg-gray-600" />
                    <span className="text-xs text-gray-500">PWA Ready</span>
                  </div>
                </div>

                {/* Coins lumineux décoratifs */}
                <div className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-pink-500 rounded-tl-lg" style={{ boxShadow: '0 0 10px rgba(236, 72, 153, 0.8)' }} />
                <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-blue-500 rounded-tr-lg" style={{ boxShadow: '0 0 10px rgba(59, 130, 246, 0.8)' }} />
                <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-blue-500 rounded-bl-lg" style={{ boxShadow: '0 0 10px rgba(59, 130, 246, 0.8)' }} />
                <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-pink-500 rounded-br-lg" style={{ boxShadow: '0 0 10px rgba(236, 72, 153, 0.8)' }} />
              </motion.div>
            </div>

            {/* 📱 Texte et infos */}
            <div className="flex-1 text-center lg:text-left">
              {/* Titre avec icône du caddy */}
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                <motion.img 
                  src="/logo-192.png" 
                  alt="LPE App" 
                  className="w-14 h-14"
                  style={{ filter: 'drop-shadow(0 0 15px rgba(0, 255, 255, 0.8))' }}
                  animate={{ 
                    filter: ['drop-shadow(0 0 15px rgba(0, 255, 255, 0.8))', 'drop-shadow(0 0 25px rgba(255, 0, 255, 0.8))', 'drop-shadow(0 0 15px rgba(0, 255, 255, 0.8))'],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <div>
                  <h3 className="text-3xl font-black text-white" style={{ textShadow: '0 0 20px rgba(0, 255, 255, 0.5)' }}>
                    📲 Télécharge <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-yellow-400">l'App</span>
                  </h3>
                  <p className="text-cyan-400 text-sm font-medium">L'Épicerie Spatiale dans ta poche !</p>
                </div>
              </div>
              
              <p className="text-gray-300 text-lg mb-5">
                🛸 <span className="text-white font-bold">Commande en 2 clics</span> depuis ton écran d'accueil. 
                <span className="text-pink-400"> L'icône s'installe automatiquement !</span>
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-3 mb-6 justify-center lg:justify-start">
                <motion.span 
                  className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(0, 255, 255, 0.05))',
                    border: '2px solid rgba(0, 255, 255, 0.5)',
                    boxShadow: '0 0 15px rgba(0, 255, 255, 0.3)',
                  }}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(0, 255, 255, 0.5)' }}
                >
                  <Zap className="w-4 h-4 text-cyan-400" /> 
                  <span className="text-cyan-300">Commande rapide</span>
                </motion.span>
                <motion.span 
                  className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.2), rgba(255, 0, 255, 0.05))',
                    border: '2px solid rgba(255, 0, 255, 0.5)',
                    boxShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
                  }}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(255, 0, 255, 0.5)' }}
                >
                  <Star className="w-4 h-4 text-pink-400" /> 
                  <span className="text-pink-300">Fidélité</span>
                </motion.span>
                <motion.span 
                  className="px-4 py-2 rounded-full text-sm font-bold"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.2), rgba(0, 255, 0, 0.05))',
                    border: '2px solid rgba(0, 255, 0, 0.5)',
                    boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)',
                  }}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(0, 255, 0, 0.5)' }}
                >
                  <span className="text-green-300">✨ 100% Gratuit</span>
                </motion.span>
              </div>

              {/* Instructions iOS/Android - Style spatial */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <motion.div 
                  className="p-4 rounded-2xl text-left"
                  style={{
                    background: 'linear-gradient(135deg, rgba(100, 100, 100, 0.2), rgba(50, 50, 50, 0.1))',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 0 20px rgba(255, 255, 255, 0.1)',
                  }}
                  whileHover={{ scale: 1.02, borderColor: 'rgba(255, 255, 255, 0.4)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🍎</span>
                    <span className="text-white font-black">iPhone / iPad</span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Safari → <span className="text-cyan-400">Partager</span> ↗️ → <span className="text-green-400">Écran d'accueil</span>
                  </p>
                </motion.div>

                <motion.div 
                  className="p-4 rounded-2xl text-left"
                  style={{
                    background: 'linear-gradient(135deg, rgba(61, 220, 132, 0.15), rgba(34, 197, 94, 0.05))',
                    border: '2px solid rgba(61, 220, 132, 0.4)',
                    boxShadow: '0 0 20px rgba(61, 220, 132, 0.2)',
                  }}
                  whileHover={{ scale: 1.02, borderColor: 'rgba(61, 220, 132, 0.6)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🤖</span>
                    <span className="text-white font-black">Android</span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Chrome → <span className="text-cyan-400">Menu ⋮</span> → <span className="text-green-400">Installer l'app</span>
                  </p>
                </motion.div>
              </div>
            </div>
          </div>

          {/* CSS pour les animations HIGH-TECH */}
          <style>{`
            @keyframes neon-border {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            @keyframes gradient-rotate {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 7: COORDONNÉES SPATIALES
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center space-y-4 pt-8 border-t border-white/10"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-cyan-400 text-xs font-bold uppercase tracking-wider">Station Active</span>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-gray-300">
            {/* Adresse */}
            <a 
              href="https://maps.google.com/?q=138+Rue+Arthur+Lamendin+62980+Vermelles"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-cyan-400 transition-colors"
            >
              <MapPin className="w-5 h-5 text-pink-400" />
              <span>138 Rue Arthur Lamendin, 62980 Vermelles</span>
            </a>

            {/* Téléphone */}
            <a 
              href="tel:0780648976"
              className="flex items-center gap-2 hover:text-green-400 transition-colors"
            >
              <Phone className="w-5 h-5 text-green-400" />
              <span className="text-cyan-400 font-semibold">07 80 64 89 76</span>
            </a>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default CommandantsSection;
